import { Amplify, Auth } from 'aws-amplify';
import React, { createContext, Dispatch, SetStateAction, useContext, useState } from 'react';
import AwsConfigAuth from '@/common/config/authConfig';
import Employee from '@/types/Employee.type';
import LoginService from '@/services/Login.service';
import { Departments } from '@/types/Department.type';
import ActSchedule from '@/types/ActSchedule.type';

Amplify.configure({ Auth: AwsConfigAuth });

interface UseAuth {
  isLoading: boolean;
  setIsLoading: Dispatch<SetStateAction<boolean>>;
  user: Employee & Departments & { actSchedule: ActSchedule };
  auth: () => Promise<boolean>;
  signIn: (
    username: string,
    password: string,
  ) => Promise<false | (Employee & Departments & { actSchedule: ActSchedule })>;
  signOut: () => void;
  forgotPassword: (username: string) => Promise<boolean>;
  forgotPasswordSubmit: (username: string, code: string, password: string) => Promise<boolean>;
}

interface Props {
  children?: React.ReactNode;
}

const authContext = createContext({} as UseAuth);

export const ProvideAuth: React.FC<Props> = ({ children }) => {
  const auth = useProvideAuth();
  return <authContext.Provider value={auth}>{children}</authContext.Provider>;
};

export const useAuth = () => useContext(authContext);

const useProvideAuth = (): UseAuth => {
  const [isLoading, setIsLoading] = useState(false);
  const [user, setUser] = useState<Employee & Departments & { actSchedule: ActSchedule }>(
    {} as Employee & Departments & { actSchedule: ActSchedule },
  );

  const auth = async () => {
    try {
      setIsLoading(true);
      const hasToken = await Auth.currentAuthenticatedUser()
        .then(() => true)
        .catch(() => false);
      if (!hasToken) {
        setIsLoading(false);
        return false;
      }
      const authed = await LoginService.getCurrentAuthenticatedUser()
        .then((response: { data: Employee & Departments & { actSchedule: ActSchedule } }) => {
          setUser(response.data);
          return true;
        })
        .catch((e: any) => {
          console.log(e);
          return false;
        });
      setIsLoading(false);
      return authed;
    } catch (error) {
      return false;
    }
  };

  const signIn = async (username: string, password: string) => {
    setIsLoading(true);
    try {
      const result = await Auth.signIn(username, password);
      if (result.challengeName === 'NEW_PASSWORD_REQUIRED') {
        await Auth.completeNewPassword(result, password).catch((e) => console.log(e));
      }

      const authed = await LoginService.getCurrentAuthenticatedUser()
        .then((response: { data: Employee & Departments & { actSchedule: ActSchedule } }) => {
          setUser(response.data);
          return response.data;
        })
        .catch((e: any) => false);

      setIsLoading(false);
      return authed as false | (Employee & Departments & { actSchedule: ActSchedule });
    } catch (error) {
      console.log(error);
      setIsLoading(false);
      return false;
    }
  };

  const signOut = async () => {
    try {
      await Auth.signOut({ global: true });
      await LoginService.logoutCurrentAuthenticateUser();
      setIsLoading(false);
      return true;
    } catch (error) {
      setIsLoading(false);
      return false;
    }
  };

  const forgotPassword = async (username: string) => {
    try {
      await Auth.forgotPassword(username);
      setIsLoading(false);
      return true;
    } catch (error) {
      console.log(error);
      setIsLoading(false);
      return false;
    }
  };

  const forgotPasswordSubmit = async (username: string, code: string, password: string) => {
    try {
      await Auth.forgotPasswordSubmit(username, code, password);
      setIsLoading(false);
      return true;
    } catch (error) {
      console.log(error);
      setIsLoading(false);
      return false;
    }
  };

  return {
    isLoading,
    setIsLoading,
    user,
    auth,
    signIn,
    signOut,
    forgotPassword,
    forgotPasswordSubmit,
  };
};
